import { Tag } from 'interfaces';
import {
  Project,
  ProjectMemberRole,
  ProjectMemberInvitationStatus,
  ProjectStatus,
  PortfolioProjectRisk
} from 'interfaces/project.interface';
import {
  MEMBER_AVATAR_COLOR_CLASSES,
  MEMBER_AVATAR_FALLBACK_INITIAL,
  MEMBER_OWNER_AVATAR_COLOR_CLASS,
  MEMBER_OWNER_AVATAR_INITIAL,
  PROJECT_DETAIL_PAGE_VIEWS,
  PROJECT_INVITE_STATUS_ICON_CLASS,
  PROJECT_STATUS_I18N_KEYS,
  type ProjectDetailPageView
} from 'constants/project';
import { ProjectDetailTab, ProjectInviteStatusVariant } from 'enums';
import i18n, { availableLanguages, t } from 'services/i18n.service';
import { uniqueByKey } from 'utils/unique-by-key.util';
import { getFutureDate, getTomorrowISODate } from 'utils/time.util';
import { v4 as uuid } from 'uuid';

const getProjectId = (project: Project): string => project.id;

export const uniqueProjectsById = (projects: Project[]): Project[] =>
  uniqueByKey(projects, getProjectId);

export const mergeProjectsById = (
  existing: Project[],
  incoming: Project[]
): Project[] => uniqueProjectsById([...existing, ...incoming]);

export const getReadyForHumanReviewLabel = (): string =>
  t(PROJECT_STATUS_I18N_KEYS.readyForHumanReview);

export const isReadyForHumanReviewLabel = (label: string): boolean =>
  availableLanguages.some(
    (language) =>
      i18n.t(PROJECT_STATUS_I18N_KEYS.readyForHumanReview, {
        lng: language
      }) === label
  );

export const getProjectDisplayName = (
  name: string | null | undefined
): string => name ?? t('todos.unnamed_project');

export const mapProjectsToTags = (projects: Project[]): Tag[] =>
  uniqueProjectsById(projects).map((project) => ({
    id: project.id,
    text: getProjectDisplayName(project.name)
  }));

export const getDefaultCreateProjectDueDate = (horizonDays: number): string => {
  const tomorrow = getTomorrowISODate();
  const fromHorizon = getFutureDate(horizonDays);
  return fromHorizon < tomorrow ? tomorrow : fromHorizon;
};

export const DEFAULT_PROJECT_STATUSES = (): ProjectStatus[] => [
  {
    id: uuid(),
    label: 'To Do',
    color: '#6B7280',
    order: 0,
    should_complete_task: false
  },
  {
    id: uuid(),
    label: 'In Progress',
    color: '#F59E0B',
    order: 1,
    should_complete_task: false
  },
  {
    id: uuid(),
    label: 'Done',
    color: '#10B981',
    order: 2,
    should_complete_task: true
  }
];

export const getRoleBadgeColor = (role: ProjectMemberRole): string => {
  switch (role) {
    case ProjectMemberRole.OWNER:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case ProjectMemberRole.ADMIN:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

export const getStatusBadgeColor = (
  status: ProjectMemberInvitationStatus
): string => {
  switch (status) {
    case ProjectMemberInvitationStatus.ACCEPTED:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case ProjectMemberInvitationStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    default:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  }
};

export const createNewProjectStatus = (
  label: string,
  order: number
): ProjectStatus => ({
  id: uuid(),
  label: label.trim(),
  color: '#6B7280',
  order,
  should_complete_task: false
});

export const updateStatusColor = (
  statuses: ProjectStatus[],
  statusId: string,
  color: string
): ProjectStatus[] => {
  return statuses.map((status) =>
    status.id === statusId ? { ...status, color } : status
  );
};

export const updateStatusLabel = (
  statuses: ProjectStatus[],
  statusId: string,
  label: string
): ProjectStatus[] => {
  return statuses.map((status) =>
    status.id === statusId ? { ...status, label } : status
  );
};

export const toggleStatusCompleteTask = (
  statuses: ProjectStatus[],
  statusId: string
): ProjectStatus[] => {
  return statuses.map((status) =>
    status.id === statusId
      ? { ...status, should_complete_task: !status.should_complete_task }
      : status
  );
};

export const removeStatus = (
  statuses: ProjectStatus[],
  statusId: string
): ProjectStatus[] => {
  return statuses.filter((status) => status.id !== statusId);
};

export const reorderStatuses = (
  statuses: ProjectStatus[],
  oldIndex: number,
  newIndex: number
): ProjectStatus[] => {
  const result = Array.from(statuses);
  const [removed] = result.splice(oldIndex, 1);
  result.splice(newIndex, 0, removed);

  return result.map((status, index) => ({
    ...status,
    order: index
  }));
};

export const getProjectsById = (
  projects: PortfolioProjectRisk[],
  ids: string[]
): PortfolioProjectRisk[] =>
  ids
    .map((id) =>
      new Map(projects.map((project) => [project.id, project])).get(id)
    )
    .filter((project): project is PortfolioProjectRisk => Boolean(project));

export const areInviteEmailsEqual = (
  inviteEmail: string | null | undefined,
  userEmail: string | null | undefined
): boolean => {
  if (!inviteEmail || !userEmail) {
    return false;
  }
  return inviteEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
};

export const shouldShowProjectMemberBadge = ({
  ownerId,
  currentUserId
}: {
  ownerId?: string;
  currentUserId?: string;
}): boolean => {
  if (!ownerId || !currentUserId) {
    return false;
  }
  return ownerId !== currentUserId;
};

export const getProjectMemberActionPermissions = ({
  viewerRole,
  targetRole,
  isTargetCurrentUser
}: {
  viewerRole?: ProjectMemberRole;
  targetRole?: ProjectMemberRole;
  isTargetCurrentUser: boolean;
}): { canInvite: boolean; canRemove: boolean; canChangeRole: boolean } => {
  const isOwner = viewerRole === ProjectMemberRole.OWNER;
  const isAdmin = viewerRole === ProjectMemberRole.ADMIN;
  const canInvite = isOwner || isAdmin;
  const denied = { canInvite, canRemove: false, canChangeRole: false };

  if (
    !canInvite ||
    isTargetCurrentUser ||
    targetRole === ProjectMemberRole.OWNER
  ) {
    return denied;
  }

  if (targetRole === ProjectMemberRole.ADMIN) {
    return { canInvite, canRemove: isOwner, canChangeRole: isOwner };
  }

  return { canInvite, canRemove: true, canChangeRole: true };
};

export const getViewerProjectRole = (
  project: Pick<Project, 'members' | 'owner_id'>,
  userId?: string
): ProjectMemberRole | undefined => {
  if (!userId) {
    return undefined;
  }
  const memberRole = project.members?.find(
    (member) => member.user_id && member.user_id === userId
  )?.role;
  if (memberRole) {
    return memberRole;
  }
  return project.owner_id === userId ? ProjectMemberRole.OWNER : undefined;
};

export const canManageProjectMembers = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER || role === ProjectMemberRole.ADMIN;

export const canViewProjectSettings = (role?: ProjectMemberRole): boolean =>
  canManageProjectMembers(role);

export const canEditProjectName = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER;

export const canLeaveProject = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.ADMIN || role === ProjectMemberRole.MEMBER;

// Task-level permissions live in `project-task-permissions.util.ts` — they track
// the server's to-do write rule (project membership + task ownership), not the
// project-admin role.

export const getProjectHeaderActionVisibility = (
  role?: ProjectMemberRole
): {
  canOpenMembers: boolean;
  canOpenSettings: boolean;
  canLeave: boolean;
} => ({
  canOpenMembers: canManageProjectMembers(role),
  canOpenSettings: canViewProjectSettings(role),
  canLeave: canLeaveProject(role)
});

export const canDeleteProject = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER;

export const isProjectDetailPageView = (
  value: string | null | undefined
): value is ProjectDetailPageView =>
  PROJECT_DETAIL_PAGE_VIEWS.some((view) => view === value);

export const resolveProjectDetailPageViewFromQuery = ({
  tab,
  noteId
}: {
  tab: string | null;
  noteId: string | null;
}): ProjectDetailPageView => {
  if (noteId != null && noteId.length > 0) {
    return ProjectDetailTab.NOTES;
  }
  if (isProjectDetailPageView(tab)) {
    return tab;
  }
  return ProjectDetailTab.TASKS;
};

export const getMemberAvatarInitial = (
  email: string | null | undefined,
  role?: ProjectMemberRole
): string => {
  const trimmedEmail = email?.trim();
  if (trimmedEmail) {
    return trimmedEmail[0].toUpperCase();
  }
  if (role === ProjectMemberRole.OWNER) {
    return MEMBER_OWNER_AVATAR_INITIAL;
  }
  return MEMBER_AVATAR_FALLBACK_INITIAL;
};

const hashAvatarSeed = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

export const getMemberAvatarColorClass = (
  email: string | null | undefined,
  role?: ProjectMemberRole
): string => {
  const trimmedEmail = email?.trim().toLowerCase();
  if (!trimmedEmail && role === ProjectMemberRole.OWNER) {
    return MEMBER_OWNER_AVATAR_COLOR_CLASS;
  }
  const seed = trimmedEmail || MEMBER_AVATAR_FALLBACK_INITIAL;
  const colorIndex = hashAvatarSeed(seed) % MEMBER_AVATAR_COLOR_CLASSES.length;
  return MEMBER_AVATAR_COLOR_CLASSES[colorIndex];
};

export const getMemberRoleLabel = (role: ProjectMemberRole): string => {
  switch (role) {
    case ProjectMemberRole.OWNER:
      return t('project.role_owner');
    case ProjectMemberRole.ADMIN:
      return t('project.role_admin');
    default:
      return t('project.role_member');
  }
};

export const getMemberInvitationStatusLabel = (
  status: ProjectMemberInvitationStatus
): string => {
  switch (status) {
    case ProjectMemberInvitationStatus.ACCEPTED:
      return t('project.status_accepted');
    case ProjectMemberInvitationStatus.PENDING:
      return t('project.status_pending');
    default:
      return t('project.status_declined');
  }
};

export const getProjectInviteStatusIconClass = (
  variant: ProjectInviteStatusVariant
): string => {
  switch (variant) {
    case ProjectInviteStatusVariant.SUCCESS:
      return PROJECT_INVITE_STATUS_ICON_CLASS.SUCCESS;
    case ProjectInviteStatusVariant.ERROR:
      return PROJECT_INVITE_STATUS_ICON_CLASS.ERROR;
    default:
      return PROJECT_INVITE_STATUS_ICON_CLASS.LOADING;
  }
};
